import React, { useState, useMemo, useEffect } from "react";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import SchemaFormView from "../../../components/Schema/SchemaFormView";
import { SchemaService, DynamicModuleService } from "../../../services/APIs";
import { removeMetaFields } from "../../../lib/removeMetaFields";
import { notify } from "../../../components/common/Notification";
import { Modal, Spin, Popconfirm, Select as AntSelect } from "antd";
import { AppLayout } from "@project/components/AppLayout";
import { type JobStatus } from "@project/data/mockData";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import navigationRoutes from "@utils/navigationRoutes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  List,
  LayoutGrid,
  Plus,
  Search,
  Pencil,
  Trash2,
  Sparkles,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Briefcase,
  MapPin,
  Users,
  BadgeCheck,
  XCircle,
  PauseCircle,
  Wand2,
  BarChart3,
  MessageSquareText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

type ViewMode = "table" | "grid";
type FilterTab = "All" | JobStatus;

const statusConfig: Record<string, { icon: any; class: string }> = {
  Draft: { icon: FileText, class: "text-muted-foreground bg-muted" },
  "Pending Approval": {
    icon: Clock,
    class: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  },
  Approved: {
    icon: BadgeCheck,
    class: "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  },
  Active: {
    icon: CheckCircle2,
    class: "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  },
  "On Hold": {
    icon: PauseCircle,
    class: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  },
  Filled: {
    icon: Users,
    class: "text-[hsl(var(--status-filled))] bg-[hsl(var(--status-filled-bg))]",
  },
  Closed: {
    icon: XCircle,
    class: "text-[hsl(var(--status-closed))] bg-[hsl(var(--status-closed-bg))]",
  },
};

const JOB_COLUMNS: string[] = [];

const JobsPage = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [createOpen, setCreateOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editJobId, setEditJobId] = useState<string | null>(null);
  const [editJobData, setEditJobData] = useState<any>(null);
  const [aiJdOpen, setAiJdOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiJdResult, setAiJdResult] = useState("");
  const [aiQuestionsOpen, setAiQuestionsOpen] = useState(false);
  const [aiQuestionsLoading, setAiQuestionsLoading] = useState(false);
  const [aiQuestionsResult, setAiQuestionsResult] = useState("");
  const [aiAnalysisOpen, setAiAnalysisOpen] = useState(false);
  const [mcqQuestions, setMcqQuestions] = useState<{ question: string; options: string[]; correct: number | null }[]>([]);
  const [aiSkillsOptions, setAiSkillsOptions] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [aiJdGenerating, setAiJdGenerating] = useState(false);
  const aiAnalysis = useAIAction();
  const aiQuestions = useAIAction();
  const navigate = useNavigate();

  const form = useMemo(() => {
    const f = createForm({});
    if (editMode && editJobData) {
      f.setInitialValues(editJobData);
      // Load MCQ questions from saved data
      try {
        const sq = editJobData.screening_questions;
        if (sq) {
          const parsed = typeof sq === "string" ? JSON.parse(sq) : sq;
          if (Array.isArray(parsed)) setMcqQuestions(parsed);
        }
      } catch { setMcqQuestions([]); }
      // Load skills from saved data
      try {
        const sk = editJobData.skills;
        if (sk) {
          const skillsArr = typeof sk === "string"
            ? sk.split(",").map((s: string) => s.trim()).filter(Boolean)
            : (Array.isArray(sk) ? sk : []);
          setSelectedSkills(skillsArr);
          setAiSkillsOptions(skillsArr);
        }
      } catch { setSelectedSkills([]); setAiSkillsOptions([]); }
    } else {
      setMcqQuestions([]);
      setSelectedSkills([]);
      setAiSkillsOptions([]);
    }
    return f;
  }, [createOpen]);
  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", "Add Job"],
    queryFn: () => SchemaService.getSchemaByName("Add Job"),
  });

  const { data: departmentsData } = useQuery({
    queryKey: ["departments-list"],
    queryFn: () => DynamicModuleService.getGridCoByConfig("Department", 1, 200, [], {}),
  });

  const departmentMap: Record<string, string> = useMemo(() => {
    const results = departmentsData?.results ?? [];
    const map: Record<string, string> = {};
    results.forEach((d: any) => {
      if (d.id) map[d.id] = d.department || d.name || d.title || d.id;
    });
    return map;
  }, [departmentsData]);

  const {
    data: jobsData,
    isLoading: jobsLoading,
    refetch: refetchJobs,
  } = useQuery({
    queryKey: ["jobs-data", page, PAGE_SIZE],
    queryFn: () =>
      DynamicModuleService.getGridCoByConfig(
        "Add Job",
        page,
        PAGE_SIZE,
        JOB_COLUMNS,
        {}
      ),
  });

  const jobs: any[] = jobsData?.results ?? [];

  const filterTabs = [
    { label: "All" },
    { label: "Active" },
    { label: "Pending Approval" },
    { label: "Draft" },
    { label: "On Hold" },
    { label: "Filled" },
    { label: "Closed" },
  ];

  const filtered = jobs.filter(j => {
    if (!search) return true;
    const lower = search.toLowerCase();
    return (
      j.job_title?.toLowerCase().includes(lower) ||
      j.location?.toLowerCase().includes(lower) ||
      j.hiring_manager?.toLowerCase().includes(lower)
    );
  });

  const handleCreateJob = async () => {
    try {
      await form.validate();
    } catch {
      return;
    }
    try {
      const values = removeMetaFields(form.values) as any;
      values.screening_questions = JSON.stringify(mcqQuestions);
      if (editMode && editJobId) {
        await DynamicModuleService.updateItem("Add Job", editJobId, values);
      } else {
        await DynamicModuleService.createItem("Add Job", values);
      }
      setCreateOpen(false);
      setEditMode(false);
      setEditJobId(null);
      setEditJobData(null);
      refetchJobs();
    } catch (error) {
      console.error("Error saving job:", error);
      notify.error(
        editMode ? "Update Failed" : "Create Failed",
        "Could not save the job. Please try again."
      );
    }
  };

  const handleEditJob = async (job: any) => {
    try {
      const jobData = await DynamicModuleService.getItemById("Add Job", job.id);
      setEditMode(true);
      setEditJobId(job.id);
      setEditJobData(jobData);
      setCreateOpen(true);
    } catch (error) {
      console.error("Error fetching job:", error);
      notify.error("Error", "Could not load job data.");
    }
  };

  const handleDeleteJob = async (job: any) => {
    try {
      await DynamicModuleService.deleteItem("Add Job", job.id);
      refetchJobs();
    } catch (error) {
      console.error("Error deleting job:", error);
      notify.error(
        "Delete Failed",
        "Could not delete the job. Please try again."
      );
    }
  };

  const handleApprove = (_jobId: string) => {
    toast({ title: "Approval Updated" });
  };

  const handleGenerateJD = async () => {
    const vals = form.values as any;
    setAiJdGenerating(true);
    try {
      const response = await fetch("https://atsai.universal-software.in/generate-jd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: vals.company_name || "",
          job_title: vals.job_title || "",
          experience_required: vals.experience_required || "",
          budget_ctc_range: vals.ctc_range || "",
          location: vals.location || "",
          job_type: vals.job_type || "",
          vacancies: vals.vacancies || 1,
          department: departmentMap[vals.departments] || vals.departments || "",
          hiring_manager: vals.hiring_manager || "",
        }),
      });
      const data = await response.json();
      if (data.job_description) {
        form.setValuesIn("job_description", data.job_description);
      }
      if (data.skills && Array.isArray(data.skills)) {
        setAiSkillsOptions(data.skills);
        setSelectedSkills(data.skills);
        form.setValuesIn("skills", data.skills.join(", "));
      }
      toast({ title: "Generated!", description: "Skills and Job Description generated successfully." });
    } catch (error) {
      console.error("Generate JD error:", error);
      notify.error("Error", "Could not generate. Please try again.");
    } finally {
      setAiJdGenerating(false);
    }
  };

  const applyAiJd = () => {
    setAiJdOpen(false);
    toast({
      title: "JD Applied",
      description: "AI-generated description applied to job form.",
    });
  };

  const handleGenerateQuestions = async () => {
    const vals = form.values as any;
    setAiQuestionsLoading(true);
    try {
      // Prepare skills array
      let skillsArray: string[] = [];
      if (vals?.skills) {
        if (typeof vals.skills === "string") {
          skillsArray = vals.skills.split(",").map((s: string) => s.trim()).filter(Boolean);
        } else if (Array.isArray(vals.skills)) {
          skillsArray = vals.skills;
        }
      }

      const response = await fetch("https://atsai.universal-software.in/generate-test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_title: vals?.job_title || "Job Position",
          experience_required: vals?.experience_required || "0 Years",
          skills: skillsArray.length > 0 ? skillsArray : ["General"],
          job_description: vals?.job_description || "Job description not provided",
        }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      
      // Parse response format: { test_title, questions: [...] }
      if (data && Array.isArray(data.questions)) {
        const parsedQuestions = data.questions.map((q: any) => {
          // Find correct answer index from options
          let correctIndex: number | null = null;
          const cleanOptions = (q.options || []).map((opt: string, idx: number) => {
            const cleanOpt = opt.trim();
            // Check if this option matches correct_answer
            if (q.correct_answer && cleanOpt === q.correct_answer.trim()) {
              correctIndex = idx;
            }
            return cleanOpt;
          });

          return {
            question: q.question || "",
            options: cleanOptions.length === 4 ? cleanOptions : ["", "", "", ""],
            correct: correctIndex,
          };
        });

        setMcqQuestions(parsedQuestions);
        toast({ 
          title: "Questions Generated!", 
          description: `${parsedQuestions.length} MCQ questions created successfully.` 
        });
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Generate Questions error:", error);
      notify.error("Error", "Could not generate questions. Please try again.");
    } finally {
      setAiQuestionsLoading(false);
    }
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Jobs
            </h1>
            <p className="mt-0.5 hidden text-[12px] text-muted-foreground sm:block">
              Manage requisitions, approvals, and job postings
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                setAiAnalysisOpen(true);
                await aiAnalysis.run(`Analyze these job postings data and provide insights:
${jobs.map(j => `- ${j.title} (${j.department}): Status=${j.status}, Applications=${j.applications}, Approval=${j.approvalStage}`).join("\n")}

Provide:
1. **Job Pipeline Health** - overview of posting statuses
2. **Department Analysis** - which departments are hiring most
3. **Bottleneck Detection** - jobs stuck in approval or with low applications
4. **Optimization Tips** - suggestions to improve job postings
5. **Priority Actions** - what to focus on immediately`);
              }}
            >
              <BarChart3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Analysis</span>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditMode(false);
                setEditJobId(null);
                setCreateOpen(true);
              }}
              className="btn-glow h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Create Requisition</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {filterTabs.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveFilter(tab.label)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all duration-200 ${activeFilter === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* Search + View Toggle */}
        <motion.div
          variants={item}
          className="flex items-center justify-between gap-2"
        >
          <div className="relative max-w-[200px] flex-1 sm:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search jobs..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
          <div className="flex overflow-hidden rounded-lg border border-border/60 bg-card">
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 transition-all ${viewMode === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 transition-all ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>

        {/* Table View */}
        <motion.div variants={item}>
          {jobsLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spin size="large" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <p className="text-[13px] text-muted-foreground">
                No jobs found.
              </p>
            </div>
          ) : viewMode === "table" ? (
            <div className="section-card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Job Title
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Department
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Experience
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Job Type
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Location
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Hiring Manager
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Vacancies
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        CTC Range
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Status
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Approval
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Applications
                      </TableHead>
                      <TableHead className="w-[100px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(job => {
                      return (
                        <TableRow key={job.id} className="table-row-hover">
                          <TableCell className="pl-5">
                            <p className="text-[13px] font-semibold text-foreground">
                              {job.job_title}
                            </p>
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.departments ? (departmentMap[job.departments] ?? job.departments) : "-"}
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.experience_required ? `${job.experience_required} Years` : "-"}
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.job_type ?? "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-[12px] text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {job.location ?? "-"}
                            </div>
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.hiring_manager ?? "-"}
                          </TableCell>
                          <TableCell className="text-[12px] font-semibold text-foreground">
                            {job.vacancies ?? "-"}
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.ctc_range ?? "-"}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const sc = statusConfig[job.status] || statusConfig.Draft;
                              const Icon = sc.icon;
                              return (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${sc.class}`}>
                                  <Icon className="h-3 w-3" />
                                  {job.status ?? "-"}
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.approval ?? "-"}
                          </TableCell>
                          <TableCell className="text-[12px] font-semibold text-foreground">
                            {job.applications ?? 0}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button 
                                className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditJob(job);
                                }}
                                title="Edit Job"
                              >
                                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                              <Popconfirm
                                title="Are you sure you want to delete this job?"
                                onConfirm={() => handleDeleteJob(job)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <button 
                                  className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Delete Job"
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                                </button>
                              </Popconfirm>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(job => {
                return (
                  <div
                    key={job.id}
                    className="section-card space-y-3 p-4 transition-all hover:shadow-md"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-[14px] font-semibold text-foreground">
                          {job.job_title}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {job.experience_required ? `${job.experience_required} Years` : "-"}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                        {job.job_type ?? "-"}
                      </span>
                    </div>
                    <div className="space-y-1.5 text-[12px] text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.location ?? "-"}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {job.hiring_manager ?? "-"} · {job.vacancies ?? 0} vacancies
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-border/30 pt-2">
                      <span className="text-[11px] text-muted-foreground">
                        CTC: {job.ctc_range ?? "-"}
                      </span>
                      <div className="flex items-center gap-1">
                        <button 
                          className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditJob(job);
                          }}
                          title="Edit Job"
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        <Popconfirm
                          title="Are you sure you want to delete this job?"
                          onConfirm={() => handleDeleteJob(job)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <button 
                            className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                            onClick={(e) => e.stopPropagation()}
                            title="Delete Job"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </Popconfirm>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Pagination */}
        {!jobsLoading && filtered.length > 0 && (
          <motion.div variants={item} className="flex items-center justify-between px-2">
            <div className="text-[12px] text-muted-foreground">
              Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, jobsData?.count ?? 0)} of {jobsData?.count ?? 0} jobs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 text-[12px]"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.ceil((jobsData?.count ?? 0) / PAGE_SIZE) }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === Math.ceil((jobsData?.count ?? 0) / PAGE_SIZE) || Math.abs(p - page) <= 1)
                  .map((p, i, arr) => {
                    const showEllipsis = i > 0 && arr[i - 1] !== p - 1;
                    return (
                      <React.Fragment key={p}>
                        {showEllipsis && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <Button
                          variant={page === p ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(p)}
                          className="h-8 w-8 p-0 text-[12px]"
                        >
                          {p}
                        </Button>
                      </React.Fragment>
                    );
                  })
                }
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(Math.ceil((jobsData?.count ?? 0) / PAGE_SIZE), p + 1))}
                disabled={page >= Math.ceil((jobsData?.count ?? 0) / PAGE_SIZE)}
                className="h-8 text-[12px]"
              >
                Next
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Create Job Modal (Ant Design - avoids Radix pointer-events conflict) */}
      <Modal
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setEditMode(false);
          setEditJobId(null);
          setEditJobData(null);
        }}
        title={editMode ? "Edit Job" : "New Job Requisition"}
        width={700}
        footer={[
          <Button
            key="cancel"
            variant="outline"
            onClick={() => {
              setCreateOpen(false);
              setEditMode(false);
              setEditJobId(null);
              setEditJobData(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="create"
            onClick={handleCreateJob}
            className="btn-glow ml-2"
          >
            {editMode ? "Update Job" : "Create Requisition"}
          </Button>,
        ]}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingTop: 8,
            scrollbarWidth: "none",
          },
        }}
      >
        {schemaLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : schemaInfo ? (
          (() => {
            const schemaJson = schemaInfo?.json ?? schemaInfo;
            const allProps = schemaJson?.schema?.properties ?? {};
            const formProps = schemaJson?.form ?? {};

            // Grid schema — exclude skills (handled separately via custom AntSelect)
            const { skills: _excludedSkills, ...jobGridPropsWithoutSkills } =
              allProps.job_grid?.properties ?? {};
            const gridSchema = {
              form: formProps,
              schema: {
                type: "object",
                properties: {
                  job_grid: {
                    ...allProps.job_grid,
                    properties: jobGridPropsWithoutSkills,
                  },
                },
              },
            };

            // JD standalone schema (no title)
            const jdSchema = {
              form: { layout: "vertical", labelCol: 24, wrapperCol: 24 },
              schema: {
                type: "object",
                properties: {
                  job_description: { ...allProps.job_description, title: "" },
                },
              },
            };

            // Screening Questions standalone schema (no title)
            const qaSchema = {
              form: { layout: "vertical", labelCol: 24, wrapperCol: 24 },
              schema: {
                type: "object",
                properties: {
                  screening_questions: {
                    ...allProps.screening_questions,
                    title: "",
                  },
                },
              },
            };

            return (
              <div className="space-y-4">
                {/* Grid fields: job_title, dept, exp, ctc, location, job_type, hiring_manager, vacancies */}
                <SchemaFormView form={form} schemaJson={gridSchema} />

                {/* Skills field — Multi-select with AI Generate */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[13px] font-medium text-foreground">
                      Skills
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ai-btn-outline h-6 gap-1 rounded-md px-2 text-[10px]"
                      disabled={aiJdGenerating}
                      onClick={handleGenerateJD}
                    >
                      {aiJdGenerating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Wand2 className="h-3 w-3" />
                      )}
                      Generate with AI
                    </Button>
                  </div>
                  <AntSelect
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Select or type skills, or use Generate with AI"
                    value={selectedSkills}
                    onChange={(vals) => {
                      setSelectedSkills(vals);
                      form.setValuesIn("skills", vals.join(", "));
                    }}
                    options={aiSkillsOptions.map(s => ({ label: s, value: s }))}
                    allowClear
                    tokenSeparators={[","]}
                  />
                </div>

                {/* Job Description field */}
                <div className="space-y-1">
                  <label className="text-[13px] font-medium text-foreground">
                    Job Description
                  </label>
                  <SchemaFormView form={form} schemaJson={jdSchema} />
                </div>

                {/* Screening Questions — MCQ Builder */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[13px] font-medium text-foreground">
                      Screening Questions
                      <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">(MCQ)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 rounded-lg text-[11px]"
                        onClick={() => setMcqQuestions(prev => [...prev, { question: "", options: ["", "", "", ""], correct: null }])}
                      >
                        <Plus className="h-3 w-3" />
                        Add Question
                      </Button>
                      <div className="relative group">
                        <Button
                          size="sm"
                          className="ai-btn h-7 gap-1 rounded-lg text-[11px]"
                          onClick={handleGenerateQuestions}
                          disabled={
                            aiQuestions.loading ||
                            !form.values?.job_title ||
                            !form.values?.experience_required ||
                            !form.values?.job_description ||
                            !form.values?.skills
                          }
                          title={
                            !form.values?.job_title || !form.values?.experience_required || !form.values?.job_description || !form.values?.skills
                              ? "Please fill Job Title, Experience, Skills & Job Description first"
                              : ""
                          }
                        >
                          {aiQuestions.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquareText className="h-3 w-3" />}
                          Generate with AI
                        </Button>
                        {(!form.values?.job_title || !form.values?.experience_required || !form.values?.job_description || !form.values?.skills) && (
                          <div className="pointer-events-none absolute bottom-full mb-2 hidden w-56 rounded-lg bg-popover px-3 py-2 text-[11px] text-popover-foreground shadow-lg group-hover:block">
                            <p className="font-semibold mb-1">Required fields:</p>
                            <ul className="space-y-0.5 text-[10px]">
                              {!form.values?.job_title && <li>• Job Title</li>}
                              {!form.values?.experience_required && <li>• Experience Required</li>}
                              {!form.values?.skills && <li>• Skills</li>}
                              {!form.values?.job_description && <li>• Job Description</li>}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {mcqQuestions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-muted-foreground/30 bg-muted/20 py-6 text-center">
                      <MessageSquareText className="mb-2 h-7 w-7 text-muted-foreground/40" />
                      <p className="text-[12px] text-muted-foreground">No screening questions yet.</p>
                      <p className="text-[11px] text-muted-foreground/70">Click "Add Question" to create an MCQ.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {mcqQuestions.map((q, qi) => (
                        <div key={qi} className="rounded-xl border border-border bg-muted/10 p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">{qi + 1}</span>
                            <input
                              className="flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                              placeholder={`Question ${qi + 1}`}
                              value={q.question}
                              onChange={e => setMcqQuestions(prev => prev.map((item, i) => i === qi ? { ...item, question: e.target.value } : item))}
                            />
                            <button
                              className="mt-1 rounded-lg p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setMcqQuestions(prev => prev.filter((_, i) => i !== qi))}
                              title="Remove"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="ml-7 grid grid-cols-2 gap-2">
                            {q.options.map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2 py-1">
                                <input
                                  className="flex-1 bg-transparent text-[11px] text-foreground placeholder:text-muted-foreground focus:outline-none"
                                  placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                                  value={opt}
                                  onChange={e => setMcqQuestions(prev => prev.map((item, i) => i === qi ? {
                                    ...item,
                                    options: item.options.map((o, j) => j === oi ? e.target.value : o)
                                  } : item))}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : (
          <div className="flex h-40 items-center justify-center text-[13px] text-muted-foreground">
            Schema "Add Job" not found. Please configure it in admin.
          </div>
        )}
      </Modal>

      {/* AI JD Dialog */}
      <Dialog open={aiJdOpen} onOpenChange={setAiJdOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" />
              AI-Generated Job Description
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Generating JD with AI...
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="max-h-[400px] overflow-y-auto whitespace-pre-wrap rounded-xl bg-muted/30 p-4 text-[13px] leading-relaxed text-foreground">
                {aiJdResult}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAiJdOpen(false)}>
                  Discard
                </Button>
                <Button onClick={applyAiJd} className="btn-glow gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Apply to Form
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Job Analysis Dialog */}
      <Dialog open={aiAnalysisOpen} onOpenChange={setAiAnalysisOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <BarChart3 className="h-5 w-5 text-primary" />
              AI Job Pipeline Analysis
            </DialogTitle>
          </DialogHeader>
          {aiAnalysis.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Analyzing job postings...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiAnalysis.result} />
          )}
        </DialogContent>
      </Dialog>

      {/* AI Questions Dialog */}
      <Dialog open={aiQuestionsOpen} onOpenChange={setAiQuestionsOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <MessageSquareText className="h-5 w-5 text-primary" />
              AI-Generated Screening Questions
            </DialogTitle>
          </DialogHeader>
          {aiQuestionsLoading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Generating screening questions...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiQuestionsResult} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default JobsPage;
